import { Component } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatTableModule } from "@angular/material/table";
import { MatListModule } from "@angular/material/list";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatChipsModule } from "@angular/material/chips";
import { ReactiveFormsModule } from "@angular/forms";
import { CaseService } from "../../../services/case.service";
import { AccountsService } from "../../../services/accounts.service";
import { getDictionaryItem } from "../../../utils/dictionary.domain";
import { CreateCaseDialogComponent } from "../../../components/case/create-case-dialog/create-case-dialog.component";
import { CaseCardComponent } from "../case-card/case-card.component";
import { MessageCenterComponent } from "../../message-center/message-center.component";
import { MessageBannerState, MessageCenter } from "../../../interfaces/messageCenter.interface";
import type { Case, FilteredCase } from "../../../interfaces/case.interface";
import { MsalService } from "@azure/msal-angular";
import { SkeletonLoaderComponent } from "../../skeleton-loader/skeleton-loader.component";
import { BusinessAreaService } from "../../../services/business-area.service";
import { BusinessArea } from "../../../interfaces/business-area-ce.interface";
import { ActivatedRoute, Router } from "@angular/router";
import { FilterComponent } from "../../filter/filter.component";
import { filterItems } from "../../../utils/filter-utils.utils";
import { FilterableItem, FilterType } from "../../../interfaces/filter.interface";
import { getStatusCodeValue } from "../../../utils/caseStatusCode.domain";

@Component({
    selector: "case-list",
    standalone: true,
    imports: [
        CaseCardComponent,
        MessageCenterComponent,
        MatTableModule,
        MatButtonModule,
        MatListModule,
        MatInputModule,
        SkeletonLoaderComponent,
        MatSelectModule,
        ReactiveFormsModule,
        MatChipsModule,
        FilterComponent
    ],
    templateUrl: "./case-list.component.html",
    styleUrl: "./case-list.component.scss"
})
export class CaseListComponent {
    public caseList?: Case[];
    public filteredCaseList?: FilteredCase[];
    public type: FilterType = FilterType.Case;
    public businessAreas?: BusinessArea[];
    public reportedByIds: string[] = [];

    displayedColumns: string[] = ["ticketnumber", "title"];
    createCaseTestId: string = "create-case-button";

    accountName: string = "";
    isLoading: boolean = true;
    reloadCases: boolean = false;
    noFoundCases = false;

    messageCenterState: MessageBannerState = MessageBannerState.success;
    showMessageCenter: boolean = false;
    messageCenter: MessageCenter = {
        title: ""
    };

    accountId: string = "";
    isDialogOpen: boolean = false;

    constructor(
        private accountService: AccountsService,
        private caseService: CaseService,
        public dialog: MatDialog,
        private authService: MsalService,
        private businessAreaService: BusinessAreaService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.accountId = typeof window !== "undefined" ? localStorage.getItem("accountid") || "" : "";
        this.businessAreaService.getAreas().subscribe((areas: BusinessArea[]) => {
            this.businessAreas = areas;
        });
        if (!this.accountId) return;
        this.accountService.getAccount(this.accountId).subscribe((account) => {
            this.accountName = account.name;
        });
        this.caseService.getCases(this.accountId).subscribe((list: Case[]) => {
            this.caseList = list;
            this.filteredCaseList = this.caseList;
            this.isLoading = false;

            if (this.caseList.length > 0) {
                this.getContactsWithCases();
            }
        });
    }

    ngOnInit() {
        this.route.queryParams.subscribe((params) => {
            if (params["creatingCase"] === "true") {
                this.openVaultDialog();
            }
        });
    }

    public openVaultDialog() {
        if (this.isDialogOpen) {
            return;
        }
        this.isDialogOpen = true;

        const dialogRef = this.dialog.open(CreateCaseDialogComponent, {
            data: { headline: getDictionaryItem("addCase") },
            width: "100%",
            height: "auto",
            maxWidth: "992px",
            maxHeight: "90vh",
            disableClose: true,
            panelClass: "case-dialog"
        });

        dialogRef.afterOpened().subscribe(() => {
            this.router.navigate([], {
                queryParams: { creatingCase: true },
                queryParamsHandling: "merge"
            });
        });

        dialogRef.afterClosed().subscribe(() => {
            this.isDialogOpen = false;
            this.router.navigate([], {
                queryParams: { creatingCase: null }
            });
        });

        dialogRef.componentInstance.caseState.subscribe((results) => {
            this.showMessageCenter = true;
            results.success ? (this.messageCenter.title = results.message) : (this.messageCenter.title = getDictionaryItem("caseErrorText"));
            results.success ? (this.messageCenterState = MessageBannerState.success) : (this.messageCenterState = MessageBannerState.error);
            setTimeout(() => {
                this.showMessageCenter = false;
            }, 5000);
        });

        dialogRef.componentInstance.caseCreated.subscribe((results) => {
            if (results.created === true) {
                this.reloadCases = true;
                setTimeout(() => {
                    this.caseService.getCase(this.accountId, results.caseId).subscribe((list: Case[]) => {
                        // the case will be returned as an array, but only one item will be in the array
                        this.caseList = [list[0], ...(this.caseList ?? [])];
                        this.filteredCaseList = [list[0], ...(this.filteredCaseList ?? [])];
                    });
                    this.reloadCases = false;
                }, 500);
            }
        });

        return dialogRef;
    }

    onFilterChanged(filter: { search: string; businessAreas: BusinessArea[]; contactId?: string[]; statuscode?: number[]; startDate?: Date; endDate?: Date }) {
        if (this.caseList) {
            const filterableCaseList: FilterableItem[] = this.caseList.map((caseItem) => ({
                name: caseItem.title,
                description: caseItem.description ?? "",
                businessAreaId: caseItem.businessareaId,
                statuscode: caseItem.statuscode,
                reportedby: caseItem.reportedbyId
            }));
            const filteredItems = filterItems(filterableCaseList, filter);

            this.filteredCaseList?.forEach((caseItem) => {
                caseItem.isFiltered = !filteredItems.some(
                    (filteredItem) => filteredItem.name === caseItem.title && filteredItem.businessAreaId === caseItem.businessareaId
                );
            });

            if (filter.contactId && filter.contactId.length > 0) {
                this.filteredCaseList?.forEach((caseItem) => {
                    caseItem.isFiltered = caseItem.isFiltered || !(filter.contactId && filter.contactId.includes(caseItem.reportedbyId ?? ""));
                });
            }

            if (filter.statuscode && filter.statuscode.length > 0) {
                this.filteredCaseList?.forEach((caseItem) => {
                    caseItem.isFiltered = caseItem.isFiltered || !(filter.statuscode && filter.statuscode.includes(caseItem.statuscode));
                });
            }

            if (filter.startDate || filter.endDate) {
                this.filteredCaseList?.forEach((caseItem) => {
                    if (caseItem.isFiltered) return;

                    const createdDate = caseItem.createdon ? new Date(caseItem.createdon) : null;
                    if (!createdDate) {
                        caseItem.isFiltered = true;
                        return;
                    }

                    const created = this.toDateOnly(createdDate);
                    const start = filter.startDate ? this.toDateOnly(filter.startDate) : null;
                    const end = filter.endDate ? this.toDateOnly(filter.endDate) : null;

                    if (start && end) {
                        caseItem.isFiltered = !(created >= start && created <= end);
                    } else if (start) {
                        caseItem.isFiltered = !(created >= start);
                    } else if (end) {
                        caseItem.isFiltered = !(created <= end);
                    }
                });
            }

            this.noFoundCases = this.filteredCaseList?.every((caseItem) => caseItem.isFiltered) ?? false;
        }
    }

    toDateOnly(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    getContactsWithCases() {
        if (!this.caseList) {
            return null;
        }
        const contactsWithCases = this.caseList
            .map((caseItem) => caseItem.reportedbyId)
            .filter((reportedById, index, self) => reportedById && self.indexOf(reportedById) === index);

        this.reportedByIds = contactsWithCases;
        return contactsWithCases;
    }

    get getActiveStatuses(): { code: number; name: string }[] {
        if (!this.caseList) return [];
        const uniqueStatusCodes = Array.from(new Set(this.caseList.map((caseItem) => caseItem.statuscode)));

        return uniqueStatusCodes
            .map((code) => ({
                code,
                name: getStatusCodeValue(code)
            }))
            .filter((item) => !!item.name);
    }

    get filterableItems(): FilterableItem[] {
        if (!this.caseList) return [];
        return (
            this.caseList?.map((caseItem) => ({
                name: caseItem.title,
                description: caseItem.description ?? "",
                businessAreaId: caseItem.businessareaId,
                statuscode: caseItem.statuscode,
                reportedby: caseItem.reportedbyId
            })) ?? []
        );
    }

    get isLoggedIn(): boolean {
        return this.authService.instance.getAllAccounts().length > 0;
    }

    getDictionaryItem = getDictionaryItem;
}
